import { useState } from "react";
import { MoreHorizontal, Gift, Users, BriefcaseIcon, Award, Sun, Moon, MapPin, ShieldCheck, Camera } from "lucide-react";
import { useCreatorStatus } from "@/hooks/useCreatorStatus";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { UserRole } from "@/utils/securityUtils";
import { useTheme } from "next-themes";

interface MobileNavMoreMenuProps {
  role: UserRole | null;
  useCompactLabels: boolean;
}

export function MobileNavMoreMenu({ role, useCompactLabels }: MobileNavMoreMenuProps) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const creator = useCreatorStatus();


  // Use onSelect (not onClick) — Radix fires onSelect reliably on touch
  // and auto-closes the menu. Avoid Radix tap-swallowing issues on mobile.
  const go = (path: string) => () => {
    setIsOpen(false);
    // Defer navigate one tick so Radix can close cleanly first.
    setTimeout(() => navigate(path), 0);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex flex-col items-center gap-1 h-auto py-2 px-2"
          style={{ touchAction: "manipulation" }}
        >
          <MoreHorizontal className="h-5 w-5" />
          <span className="text-[10px]">{useCompactLabels ? "More" : "More"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" style={{ touchAction: "manipulation" }}>
        <DropdownMenuItem onSelect={go('/connections')}>
          <Users className="mr-2 h-4 w-4" />
          Connections
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={go('/certificates')}>
          <Award className="mr-2 h-4 w-4" />
          My Certificates
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={go('/connections')}>
          <Gift className="mr-2 h-4 w-4" />
          My Circle
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={go('/wishlist')}>
          🎁 <span className="ml-2">My Gift List</span>
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={go('/city-tasks')}>
          <MapPin className="mr-2 h-4 w-4" />
          City Tasks
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={go(creator.to)}>
          <Camera className="mr-2 h-4 w-4" />
          {creator.label}
        </DropdownMenuItem>


        <DropdownMenuItem
          onSelect={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
        >
          {resolvedTheme === "dark" ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
          {resolvedTheme === "dark" ? "Dark Mode" : "Light Mode"}
        </DropdownMenuItem>

        {role === 'advertiser' && (
          <DropdownMenuItem onSelect={go('/advertiser-dashboard')}>
            <BriefcaseIcon className="mr-2 h-4 w-4" />
            Advertiser Dashboard
          </DropdownMenuItem>
        )}

        {role === 'admin' && (
          <DropdownMenuItem onSelect={go('/admin/partner-analytics')}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Admin Panel
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
